import { z } from 'zod';
import { getComponentDefinition } from './components';
import { resourceKeySchema, timestampSchema } from './common';
import type { FieldError } from './errors';
import type { Permission } from './permissions';

/**
 * Saved views (architecture documentation section 9.2).
 *
 * A view is versioned configuration, never executable code. That is what makes
 * it safe to store user-authored layouts, validate them server-side, and
 * migrate them forward without a deployment coupling.
 */

/** Bump when the document shape changes, and add the matching migration below. */
export const CURRENT_VIEW_SCHEMA_VERSION = 2;

export const componentInstanceSchema = z.object({
  instanceId: z.string().min(1).max(64),
  componentType: z.string().min(1).max(64),
  /** Validated against the per-type schema in a second pass; see validateViewDefinition. */
  configuration: z.record(z.string(), z.unknown()).default({}),
  titleOverride: z.string().max(120).nullish(),
  position: z.object({
    x: z.number().int().min(0).max(11),
    y: z.number().int().min(0).max(199),
    width: z.number().int().min(1).max(12),
    height: z.number().int().min(1).max(12),
  }),
});
export type ComponentInstance = z.infer<typeof componentInstanceSchema>;

export const viewDefinitionSchema = z
  .object({
    schemaVersion: z.number().int().min(1),
    layout: z.object({
      type: z.literal('responsive-grid'),
      columns: z.literal(12),
    }),
    /** Bounded: an unbounded view would produce an unbounded dashboard load (section 15.1). */
    components: z.array(componentInstanceSchema).max(40),
  })
  .strict();
export type ViewDefinition = z.infer<typeof viewDefinitionSchema>;

export const viewVisibilitySchema = z.enum(['private', 'shared']);
export type ViewVisibility = z.infer<typeof viewVisibilitySchema>;

export const viewSchema = z.object({
  id: z.string().uuid(),
  slug: resourceKeySchema,
  title: z.string(),
  visibility: viewVisibilitySchema,
  ownerUserId: z.string().uuid(),
  isOwner: z.boolean(),
  definition: viewDefinitionSchema,
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type View = z.infer<typeof viewSchema>;

export const viewSummarySchema = viewSchema.omit({ definition: true });
export type ViewSummary = z.infer<typeof viewSummarySchema>;

export const createViewSchema = z
  .object({
    slug: resourceKeySchema,
    title: z.string().min(1).max(120),
    visibility: viewVisibilitySchema.default('private'),
    definition: viewDefinitionSchema,
  })
  .strict();
export type CreateView = z.infer<typeof createViewSchema>;

export const updateViewSchema = z
  .object({
    title: z.string().min(1).max(120).optional(),
    visibility: viewVisibilitySchema.optional(),
    definition: viewDefinitionSchema.optional(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type UpdateView = z.infer<typeof updateViewSchema>;

export const emptyViewDefinition = (): ViewDefinition => ({
  schemaVersion: CURRENT_VIEW_SCHEMA_VERSION,
  layout: { type: 'responsive-grid', columns: 12 },
  components: [],
});

/* -------------------------------------------------------------------------- */
/* Schema migration                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Migrations are applied on read, oldest first, until the document reaches the
 * current version. Without this, the first schema change would strand every
 * previously saved view -- the gap in the original section 9.2, which declared
 * a `schemaVersion` but no way to act on it.
 *
 * Each entry migrates *from* its key *to* key + 1 and must be pure and total:
 * it receives whatever was persisted, including shapes that no longer validate.
 *
 * Example of the next one, when component positions gain a `layer`:
 *
 *   2: (doc) => ({
 *     ...doc,
 *     schemaVersion: 3,
 *     components: doc.components.map((c) => ({ ...c, position: { ...c.position, layer: 0 } })),
 *   }),
 */
export type ViewMigration = (document: Record<string, unknown>) => Record<string, unknown>;

/**
 * Component types and configuration fields that the single-project redesign
 * removed, and what a saved document does about them.
 *
 * A stored view naming a component the catalog no longer has fails validation,
 * and a view that fails validation is served empty -- so every reader of a
 * dashboard built before the change would have lost the whole dashboard, not
 * just the tile that went away. Dropping the affected instances here is the
 * difference between one missing tile and a blank page.
 */
const REMOVED_COMPONENT_TYPES = new Set(['project-table', 'project-timeline']);
const REMOVED_METRICS = new Set(['projects.active', 'projects.at_risk']);

export const VIEW_MIGRATIONS: Record<number, ViewMigration> = {
  /**
   * 1 -> 2: the portfolio components leave, and `schedule-overview` stops
   * naming a project.
   *
   * The schedule tile now binds to the workspace's project, so a stored
   * `projectId` is not merely unused -- `.strict()` would reject the whole
   * configuration and take the tile with it. Stripping the key keeps the tile
   * and lets it bind the way every other project-scoped surface does.
   */
  1: (document) => ({
    ...document,
    schemaVersion: 2,
    components: (Array.isArray(document.components) ? document.components : [])
      .filter((component) => {
        if (typeof component !== 'object' || component === null) return true;
        const instance = component as Record<string, unknown>;
        if (REMOVED_COMPONENT_TYPES.has(String(instance.componentType))) return false;
        if (instance.componentType !== 'kpi-metric') return true;
        const configuration = instance.configuration;
        if (typeof configuration !== 'object' || configuration === null) return true;
        return !REMOVED_METRICS.has(String((configuration as Record<string, unknown>).metric));
      })
      .map((component: unknown) => {
        if (typeof component !== 'object' || component === null) return component;
        const instance = component as Record<string, unknown>;
        if (instance.componentType !== 'schedule-overview') return instance;
        const { projectId: _dropped, ...configuration } = (instance.configuration ?? {}) as Record<
          string,
          unknown
        >;
        return { ...instance, configuration };
      }),
  }),
};

export function migrateViewDefinition(raw: unknown): Record<string, unknown> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new Error('View definition must be an object');
  }
  let document = raw as Record<string, unknown>;
  let version = typeof document.schemaVersion === 'number' ? document.schemaVersion : 0;

  if (version > CURRENT_VIEW_SCHEMA_VERSION) {
    // A document written by a newer deployment. Refusing is safer than
    // silently discarding fields we do not understand.
    throw new ViewSchemaUnsupportedError(version);
  }

  while (version < CURRENT_VIEW_SCHEMA_VERSION) {
    const migration = VIEW_MIGRATIONS[version];
    if (!migration) throw new ViewSchemaUnsupportedError(version);
    document = migration(document);
    version += 1;
  }
  return document;
}

export class ViewSchemaUnsupportedError extends Error {
  constructor(readonly schemaVersion: number) {
    super(`Unsupported view schema version ${schemaVersion}`);
    this.name = 'ViewSchemaUnsupportedError';
  }
}

/* -------------------------------------------------------------------------- */
/* Validation                                                                  */
/* -------------------------------------------------------------------------- */

export type ViewValidationResult =
  | {
      ok: true;
      definition: ViewDefinition;
      requiredPermissions: Permission[];
      /**
       * The distinct component types the finished view uses, so the caller can
       * also refuse a view referencing a component this deployment does not
       * have (contracts: modules.ts). Kept beside `requiredPermissions` because
       * it answers the same shape of question -- what does saving this commit
       * us to? -- against the other of the two gates.
       */
      componentTypes: string[];
    }
  | { ok: false; errors: FieldError[] };

/**
 * Two-pass validation. The structural pass checks the document shape; the
 * second pass validates each component's configuration against its own schema
 * from the catalog, which the structural schema cannot express.
 *
 * Returns the permissions the finished view depends on, so the caller can
 * refuse to save a view referencing components the actor may not use.
 */
export function validateViewDefinition(raw: unknown): ViewValidationResult {
  const structural = viewDefinitionSchema.safeParse(raw);
  if (!structural.success) {
    return { ok: false, errors: zodErrorToFieldErrors(structural.error) };
  }

  const definition = structural.data;
  const errors: FieldError[] = [];
  const seenInstanceIds = new Set<string>();
  const requiredPermissions = new Set<Permission>();
  const componentTypes = new Set<string>();
  const components: typeof definition.components = [];

  definition.components.forEach((instance, index) => {
    const pointer = `/components/${index}`;

    if (seenInstanceIds.has(instance.instanceId)) {
      errors.push({
        pointer: `${pointer}/instanceId`,
        code: 'view.duplicate_instance_id',
        params: { instanceId: instance.instanceId },
      });
      return;
    }
    seenInstanceIds.add(instance.instanceId);

    const definitionForType = getComponentDefinition(instance.componentType);
    if (!definitionForType) {
      errors.push({
        pointer: `${pointer}/componentType`,
        code: 'view.component_unknown',
        params: { componentType: instance.componentType },
      });
      return;
    }

    if (
      instance.position.width < definitionForType.minSize.width ||
      instance.position.height < definitionForType.minSize.height
    ) {
      errors.push({
        pointer: `${pointer}/position`,
        code: 'view.component_below_minimum_size',
        params: {
          minWidth: definitionForType.minSize.width,
          minHeight: definitionForType.minSize.height,
        },
      });
    }

    if (instance.position.x + instance.position.width > definition.layout.columns) {
      errors.push({
        pointer: `${pointer}/position`,
        code: 'view.component_out_of_bounds',
        params: { columns: definition.layout.columns },
      });
    }

    const configuration = definitionForType.configSchema.safeParse(instance.configuration);
    if (!configuration.success) {
      errors.push(
        ...zodErrorToFieldErrors(configuration.error).map((error) => ({
          ...error,
          pointer: `${pointer}/configuration${error.pointer}`,
        })),
      );
      return;
    }

    for (const permission of definitionForType.requiredPermissions) {
      requiredPermissions.add(permission);
    }
    componentTypes.add(instance.componentType);
    // Keep the parsed configuration: defaults are now materialized, so what we
    // persist is exactly what the renderer will receive.
    components.push({ ...instance, configuration: configuration.data as Record<string, unknown> });
  });

  if (errors.length > 0) return { ok: false, errors };

  return {
    ok: true,
    definition: { ...definition, schemaVersion: CURRENT_VIEW_SCHEMA_VERSION, components },
    requiredPermissions: [...requiredPermissions],
    componentTypes: [...componentTypes],
  };
}

/** Converts a Zod issue list into JSON Pointer field errors for the problem document. */
export function zodErrorToFieldErrors(error: z.ZodError): FieldError[] {
  return error.issues.map((issue) => ({
    pointer: '/' + issue.path.map((segment) => String(segment)).join('/'),
    code: `validation.${issue.code}`,
    params: { message: issue.message },
  }));
}
